import { apiClient } from './api';

// ============================================
// Auth Service — OTP verification API calls
// ============================================

// Response shapes match the flat JSON the API returns

interface ApiError {
  response?: { status: number; data: Record<string, unknown> };
}

export interface PendingChallenge {
  channel: 'WhatsApp' | 'Sms' | string;
  maskedPhone: string;
  expiresAt: string;
}

interface OtpApiData {
  challengeId?: string;
  expiresAt?: string;
  maskedPhone?: string;
  maskedEmail?: string;
  message?: string;
  deliveryChannel?: 'WhatsApp' | 'Sms' | 'Dev';
  verified?: boolean;
  trustLevel?: number;
  officialGuestId?: string;
  trustLevelName?: string;
  isVerified?: boolean;
  verifiedAt?: string;
  verificationMethod?: string;
  remainingAttempts?: number;
  error?: string;
  hasPhoneOnFile?: boolean;
  hasEmailOnFile?: boolean;
  pendingChallenge?: PendingChallenge | null;
}

export interface OtpRequestResponse {
  success: boolean;
  challengeId?: string;
  expiresAt?: string;
  maskedPhone?: string;
  message?: string;
  error?: string;
  deliveryChannel?: 'WhatsApp' | 'Sms' | 'Dev';
}

export interface OtpVerifyResponse {
  success: boolean;
  verified?: boolean;
  trustLevel?: number;
  officialGuestId?: string;
  message?: string;
  error?: string;
  remainingAttempts?: number;
}

export interface AuthStatusResponse {
  success: boolean;
  trustLevel?: number;
  trustLevelName?: string;
  isVerified?: boolean;
  verifiedAt?: string;
  verificationMethod?: string;
  /** Set when an OTP was already sent (e.g. pushed by front desk) and is awaiting entry. */
  pendingChallenge?: PendingChallenge | null;
}

export interface PhoneHintResponse {
  hasPhoneOnFile: boolean;
  maskedPhone?: string;
}

export interface EmailHintResponse {
  hasEmailOnFile: boolean;
  maskedEmail?: string;
}

/**
 * Request an OTP code to be sent via SMS.
 */
export async function requestOtp(phone: string): Promise<OtpRequestResponse> {
  try {
    const response = await apiClient.post<OtpApiData>('/api/auth/sms/request', { phone });
    const d = response.data;
    return {
      success: true,
      challengeId: d.challengeId,
      expiresAt: d.expiresAt,
      maskedPhone: d.maskedPhone,
      message: d.message,
      deliveryChannel: d.deliveryChannel,
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    if (e.response?.status === 429) {
      return {
        success: false,
        error: (data?.error as string) || 'Too many attempts. Please wait before trying again.',
      };
    }
    return {
      success: false,
      error: (data?.error as string) || 'Failed to send verification code.',
    };
  }
}

/**
 * Verify an OTP code against a challenge. When challengeId is omitted (e.g. the code was
 * pushed by staff so the app never created a challenge), the server verifies against the
 * latest pending session-level challenge instead.
 */
export async function verifyOtp(challengeId: string | null | undefined, code: string): Promise<OtpVerifyResponse> {
  try {
    const payload: Record<string, unknown> = { code };
    if (challengeId) payload.challengeId = challengeId;
    const response = await apiClient.post<OtpApiData>('/api/auth/sms/verify', payload);
    const d = response.data;
    return {
      success: true,
      verified: d.verified,
      trustLevel: d.trustLevel,
      officialGuestId: d.officialGuestId,
      message: d.message,
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    return {
      success: false,
      error: (data?.error as string) || 'Verification failed.',
      remainingAttempts: data?.remainingAttempts as number | undefined,
    };
  }
}

/**
 * Request a DEVICE-level WhatsApp OTP to the room phone (the "verify this device to order" flow).
 * Requires the SESSION to already be PhoneVerified — the room phone is on file and the code goes to it.
 * On verify the device's TrustedAt is set, so it may order/charge.
 */
export async function requestDeviceOtp(phone: string): Promise<OtpRequestResponse> {
  try {
    const response = await apiClient.post<OtpApiData>('/api/auth/whatsapp/device/request', { phone });
    const d = response.data;
    return {
      success: true,
      challengeId: d.challengeId,
      expiresAt: d.expiresAt,
      maskedPhone: d.maskedPhone,
      message: d.message,
      deliveryChannel: d.deliveryChannel,
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    if (e.response?.status === 429) {
      return {
        success: false,
        error: (data?.error as string) || 'Too many attempts. Please wait before trying again.',
      };
    }
    return {
      success: false,
      error: (data?.error as string) || 'Failed to send verification code.',
    };
  }
}

/**
 * Verify a DEVICE-level WhatsApp OTP. On success this device's TrustedAt is set (it may order/charge).
 */
export async function verifyDeviceOtp(challengeId: string | null | undefined, code: string): Promise<OtpVerifyResponse> {
  try {
    const response = await apiClient.post<OtpApiData>('/api/auth/whatsapp/device/verify', {
      challengeId,
      code,
    });
    const d = response.data;
    return {
      success: true,
      verified: d.verified,
      message: d.message,
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    return {
      success: false,
      error: (data?.error as string) || 'Verification failed.',
      remainingAttempts: data?.remainingAttempts as number | undefined,
    };
  }
}

/**
 * Get current auth/trust level status.
 */
export async function getAuthStatus(): Promise<AuthStatusResponse> {
  try {
    const response = await apiClient.get<OtpApiData>('/api/auth/status');
    const d = response.data;
    return {
      success: true,
      trustLevel: d.trustLevel,
      trustLevelName: d.trustLevelName,
      isVerified: d.isVerified,
      verifiedAt: d.verifiedAt,
      verificationMethod: d.verificationMethod,
      pendingChallenge: d.pendingChallenge ?? null,
    };
  } catch {
    return { success: false };
  }
}

/**
 * Check if the session has a phone number on file that must be matched for OTP.
 */
export async function getPhoneHint(): Promise<PhoneHintResponse> {
  try {
    const response = await apiClient.get<OtpApiData>('/api/auth/sms/phone-hint');
    return { hasPhoneOnFile: response.data.hasPhoneOnFile ?? false, maskedPhone: response.data.maskedPhone };
  } catch {
    return { hasPhoneOnFile: false };
  }
}

/**
 * Check if the session has an email on file for verification.
 */
export async function getEmailHint(): Promise<EmailHintResponse> {
  try {
    const response = await apiClient.get<OtpApiData>('/api/auth/email/email-hint');
    return { hasEmailOnFile: response.data.hasEmailOnFile ?? false, maskedEmail: response.data.maskedEmail };
  } catch {
    return { hasEmailOnFile: false };
  }
}

/**
 * Request an OTP code to be sent via email.
 */
export async function requestEmailOtp(email: string): Promise<OtpRequestResponse> {
  try {
    const response = await apiClient.post<OtpApiData>('/api/auth/email/request', { email });
    const d = response.data;
    return {
      success: true,
      challengeId: d.challengeId,
      expiresAt: d.expiresAt,
      maskedPhone: d.maskedEmail, // Reuse same field for masked destination
      message: d.message,
      deliveryChannel: 'Sms', // Will show as email in UI
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    if (e.response?.status === 429) {
      return {
        success: false,
        error: (data?.error as string) || 'Too many attempts. Please wait before trying again.',
      };
    }
    return {
      success: false,
      error: (data?.error as string) || 'Failed to send verification code.',
    };
  }
}

// ============================================
// Token-less sign-in by phone code
// (no session required — the code mints a session)
// ============================================

export interface SignInScope {
  hotelId?: number | null;
  chainId?: number | null;
}

export interface SignInVerifyData {
  token: string;
  sessionId: string;
  hotelSlug: string;
  hotelName?: string;
  expiresAt?: string;
  checkOutAt?: string;
  hotelTimeZone?: string;
  defaultLanguage?: string;
}

export interface DeviceInfoInput {
  deviceName?: string;
  screenSize?: string;
  language?: string;
  timezone?: string;
  userAgent?: string;
}

/**
 * Step 1 — request a code to the on-file phone of an active stay.
 * The backend always responds generically (never reveals whether a stay matched).
 */
export async function signInRequest(phone: string, scope: SignInScope): Promise<{ sent: boolean; error?: string }> {
  try {
    await apiClient.post('/api/v1/guest/signin/request', {
      phone,
      hotelId: scope.hotelId ?? undefined,
      chainId: scope.chainId ?? undefined,
    });
    return { sent: true };
  } catch {
    return { sent: false, error: 'Could not send a code. Please try again.' };
  }
}

/**
 * Step 2 — verify the code; on success the server mints a session and returns an activation
 * payload (token, sessionId, hotelSlug, …) identical in shape to /activate.
 */
export async function signInVerify(
  phone: string,
  code: string,
  scope: SignInScope,
  deviceInfo: DeviceInfoInput,
): Promise<{ success: boolean; data?: SignInVerifyData; error?: string }> {
  try {
    const response = await apiClient.post<Record<string, unknown>>('/api/v1/guest/signin/verify', {
      phone,
      code,
      hotelId: scope.hotelId ?? undefined,
      chainId: scope.chainId ?? undefined,
      deviceInfo,
    });
    const raw = response.data as Record<string, unknown>;
    const d = ((raw.data as Record<string, unknown>) || raw) as unknown as SignInVerifyData;
    return { success: true, data: d };
  } catch (err: unknown) {
    const e = err as ApiError;
    return { success: false, error: (e.response?.data?.error as string) || 'Invalid or expired code.' };
  }
}

/**
 * Verify an email OTP code against a challenge.
 */
export async function verifyEmailOtp(challengeId: string, code: string): Promise<OtpVerifyResponse> {
  try {
    const response = await apiClient.post<OtpApiData>('/api/auth/email/verify', {
      challengeId,
      code,
    });
    const d = response.data;
    return {
      success: true,
      verified: d.verified,
      trustLevel: d.trustLevel,
      officialGuestId: d.officialGuestId,
      message: d.message,
    };
  } catch (err: unknown) {
    const e = err as ApiError;
    const data = e.response?.data;
    return {
      success: false,
      error: (data?.error as string) || 'Verification failed.',
      remainingAttempts: data?.remainingAttempts as number | undefined,
    };
  }
}
